import React from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { WeeklyHours, DayKey, DAY_KEYS, DAY_LABELS } from './prestationPlanningShared';

interface Salarie { id: string; nom: string; perso: boolean; weekly_hours: WeeklyHours }

const champHeure = 'bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500';

/**
 * Horaires de chaque salarié (2026-10-18, audit concurrents : Fresha, Booksy). Par défaut, un salarié suit les
 * horaires de l'établissement. Dès qu'on lui règle des horaires propres (jours travaillés, heures, pause), les
 * créneaux réservables en ligne ne comptent sa disponibilité que sur ces plages.
 */
export function HorairesEquipePage() {
    const [salaries, setSalaries] = React.useState<Salarie[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [busy, setBusy] = React.useState('');
    const [saved, setSaved] = React.useState('');

    const charger = React.useCallback(async () => {
        setLoading(true); setErreur('');
        try {
            const res: any = await moduleApi.getPrestationEmployeeHours();
            if (res?.error) throw new Error(res.error);
            setSalaries(Array.isArray(res?.salaries) ? res.salaries : []);
        } catch (e: any) { setErreur(e?.message || 'Chargement impossible.'); }
        finally { setLoading(false); }
    }, []);
    React.useEffect(() => { charger(); }, [charger]);

    const maj = (id: string, patch: Partial<Salarie>) => setSalaries((l) => l.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const majJour = (s: Salarie, jour: DayKey, patch: Partial<WeeklyHours[DayKey]>) => maj(s.id, { weekly_hours: { ...s.weekly_hours, [jour]: { ...s.weekly_hours[jour], ...patch } } });

    const enregistrer = async (s: Salarie) => {
        setBusy(s.id); setErreur(''); setSaved('');
        try {
            const res: any = await moduleApi.setPrestationEmployeeHours(s.id, s.perso ? s.weekly_hours : null);
            if (res?.error) throw new Error(res.error);
            setSaved(s.nom);
            await charger();
        } catch (e: any) { setErreur(e?.message || "L'enregistrement a échoué."); }
        finally { setBusy(''); }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-5">
            <div>
                <h1 className="text-xl font-black text-content flex items-center gap-2.5"><Clock size={22} className="text-brand-600 dark:text-brand-400" /> Horaires de l'équipe</h1>
                <p className="text-sm text-content-muted mt-1">Un salarié suit les horaires de l'établissement, sauf si vous lui en réglez de propres. Les créneaux proposés aux clients en ligne tiennent compte de ces horaires.</p>
            </div>
            {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
            {saved && !erreur && <p className="text-xs text-green-600 dark:text-green-400 font-semibold">Horaires enregistrés pour {saved}.</p>}
            {loading ? (
                <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-content-muted" /></div>
            ) : salaries.length === 0 ? (
                <p className="text-sm text-content-muted text-center py-12">Aucun salarié pour le moment.</p>
            ) : (
                <div className="space-y-3">
                    {salaries.map((s) => (
                        <div key={s.id} className="border border-border rounded-xl p-4 space-y-3 bg-surface">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="font-black text-content">{s.nom}</p>
                                <label className="flex items-center gap-2 text-xs font-bold text-content cursor-pointer">
                                    <input type="checkbox" className="accent-brand-600" checked={s.perso} onChange={(e) => maj(s.id, { perso: e.target.checked })} />
                                    Horaires personnalisés
                                </label>
                            </div>
                            {!s.perso ? (
                                <p className="text-xs text-content-muted">Suit les horaires de l'établissement.</p>
                            ) : (
                                <div className="space-y-2">
                                    {DAY_KEYS.map((jour) => {
                                        const cfg = s.weekly_hours[jour];
                                        return (
                                            <div key={jour} className="flex flex-wrap items-center gap-3 p-2 rounded-lg bg-surface-soft border border-border">
                                                <span className="w-24 shrink-0 text-xs font-bold text-content">{DAY_LABELS[jour]}</span>
                                                <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer">
                                                    <input type="checkbox" className="accent-brand-600" checked={cfg.closed} onChange={(e) => majJour(s, jour, { closed: e.target.checked })} />
                                                    Ne travaille pas
                                                </label>
                                                {!cfg.closed && (
                                                    <>
                                                        <input type="time" className={champHeure} value={cfg.opening} onChange={(e) => majJour(s, jour, { opening: e.target.value })} />
                                                        <span className="text-xs text-content-muted">à</span>
                                                        <input type="time" className={champHeure} value={cfg.closing} onChange={(e) => majJour(s, jour, { closing: e.target.value })} />
                                                        <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer">
                                                            <input type="checkbox" className="accent-brand-600" checked={!!cfg.hasBreak}
                                                                onChange={(e) => majJour(s, jour, e.target.checked ? { hasBreak: true, breakStart: cfg.breakStart || '12:00', breakEnd: cfg.breakEnd || '14:00' } : { hasBreak: false })} />
                                                            Pause
                                                        </label>
                                                        {cfg.hasBreak && (
                                                            <>
                                                                <input type="time" className={champHeure} value={cfg.breakStart || ''} onChange={(e) => majJour(s, jour, { breakStart: e.target.value })} />
                                                                <span className="text-xs text-content-muted">–</span>
                                                                <input type="time" className={champHeure} value={cfg.breakEnd || ''} onChange={(e) => majJour(s, jour, { breakEnd: e.target.value })} />
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <button type="button" onClick={() => enregistrer(s)} disabled={busy === s.id}
                                className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50">
                                {busy === s.id ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
